import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  InputAdornment,
  TextField,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AxiosConfig from '../../../config/AxiosConfig';
import { setSnackBar } from '../../states/app/staticSlice.js';
import ErrorBoundary from '../utils/errors/ErrorBoundary';
import InternalServerError from '../utils/errors/InternalServerError.jsx';

// Débouncer la recherche pour éviter des requêtes répétées
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const SearchBar = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [searchResult, setSearchResult] = useState([]);
  const [displayedResult, setDisplayedResult] = useState([]);
  const [_focus, setFocus] = useState(false);
  const dispatch = useDispatch();
  const theme = useTheme(); // Utiliser le thème global

  // Débouncer la valeur de recherche pour améliorer la performance
  const debouncedSearchText = useDebounce(searchText, 500);

  // Gestion du texte de recherche
  const handleChangeSearch = (event) => {
    setSearchText(event.target.value);
  };

  // Gestion du choix d'un élément de recherche, redirige vers le projet correspondant
  const handleSelectSearch = (event, value) => {
    if (value?.projectId && value?.ticketId) {
      navigate(
        `/project/${value?.projectId}/tickets/${value?.ticketId}/activities`
      );
    } else if (value?.projectId) {
      navigate(`/project/${value?.projectId}/tickets/`);
    }
  };

  const handleResponse = (res) => {
    if (res.status > 300) {
      const messageError = `Error: ${res.status} - ${res.statusText}: /search/`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'error' })
      );
    }
  };

  // Effet pour récupérer les résultats en fonction du texte recherché
  useEffect(() => {
    if (debouncedSearchText?.trim().length > 0) {
      AxiosConfig.create('post', `/search`)
        .call({
          searchText: debouncedSearchText?.trim(),
        })
        .exit((res) => {
          setSearchResult(res?.data || []);
          handleResponse(res);
        })
        .catch((err) => console.error(err));
    } else {
      setSearchResult([]);
    }
  }, [debouncedSearchText]);

  // Mise à jour des résultats affichés
  useEffect(() => {
    setDisplayedResult(
      searchResult?.map((res, index) => ({
        id: `${res?.projectId}-${res?._id || index}`,
        projectId: res?.projectId,
        ticketId: res?.type === 'T' ? res?._id : null,
        label: res?.content ?? '',
      }))
    );
  }, [searchResult]);

  // Gestion du focus pour relancer la recherche au retour du focus
  const handleFocus = () => {
    setFocus(true);
    if (debouncedSearchText.trim()) {
      AxiosConfig.create('post', `/search`)
        .call({
          searchText: debouncedSearchText?.trim(),
        })
        .exit((res) => {
          setSearchResult(res.data);
        })
        .catch((err) => console.error(err));
    }
  };

  const handleBlur = () => {
    setFocus(false);
  };

  return (
    <ErrorBoundary fallback={<InternalServerError />} source="Search Bar">
      <Autocomplete
        freeSolo
        autoHighlight
        options={displayedResult}
        getOptionLabel={(option) => {
          return option?.label || option;
        }}
        onChange={handleSelectSearch}
        onFocus={handleFocus} // Ajout de l'événement focus
        onBlur={handleBlur} // Ajout de l'événement blur
        renderInput={(params) => (
          <TextField
            {...params}
            onChange={handleChangeSearch}
            value={searchText}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px', // Coins arrondis
                backgroundColor: theme.palette.background.paper, // Arrière-plan basé sur le thème
                '&:hover': {
                  borderColor: theme.palette.primary.main, // Bordure au survol
                },
                '&.Mui-focused': {
                  borderColor: theme.palette.primary.main, // Bordure en focus
                },
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary, // Couleur de l'étiquette en mode sombre/claire
              },
              '& .MuiInputAdornment-root': {
                color: theme.palette.text.secondary, // Couleur de l'icône
              },
            }}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                type: 'text',
              },
            }}
          />
        )}
      />
    </ErrorBoundary>
  );
};

export default SearchBar;
